"use client";

import Masonry from 'react-masonry-css';
import { CatalogCard } from '../catalog-card';
import './masonry-grid.css';
import { motion } from 'framer-motion';
import type { CatalogItem, CatalogType } from "@/lib/services/catalog/catalog-types";

interface MasonryGridProps {
  artworks: CatalogItem[];
  viewMode?: 'grid4' | 'grid6' | 'grid8';
  columns?: number;
  onView?: (artwork: CatalogItem) => void;
  onEnlarge: (artwork: CatalogItem) => void;
  onMouseEnter?: (artwork: CatalogItem) => void;
  onMouseLeave?: () => void;
  onMouseMove?: (e: React.MouseEvent) => void;
  onCopyWallet?: (walletAddress: string | undefined) => void;
  copiedWallet?: string | null;
  catalogType?: CatalogType;
  useAspectRatio?: boolean;
}

export function MasonryGrid({
  artworks,
  viewMode = 'grid4',
  columns = 4,
  onView,
  onEnlarge,
  onMouseEnter,
  onMouseLeave,
  onMouseMove,
  onCopyWallet,
  copiedWallet,
  catalogType = 'all',
  useAspectRatio = true
}: MasonryGridProps) {
  // Determine column count based on viewMode or columns prop
  const getColumnCount = () => {
    if (viewMode === 'grid8') return 8;
    if (viewMode === 'grid4') return 4;
    return columns;
  };

  const series = artworks.filter(item => item.type === 'collection');
  const separateSeries = series.length > 0 && artworks.some(item => item.type === 'artwork');
  const renderGrid = (items: CatalogItem[]) => (
    <Masonry
      breakpointCols={{
        default: getColumnCount(),
        1100: getColumnCount() > 4 ? 4 : getColumnCount(),
        700: 2,
        500: 1
      }}
      className="my-masonry-grid"
      columnClassName="my-masonry-grid-column"
    >
      {items.map((artwork, index) => (
        <motion.div 
          key={`${artwork.id}-${index}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.5, 
            delay: index * 0.05, // Staggered delay based on index
            ease: [0.25, 0.1, 0.25, 1.0] // Custom easing curve
          }}
          className="masonry-item-container"
        >
          <CatalogCard
            artwork={artwork}
            onView={onView}
            onEnlarge={onEnlarge}
            onMouseEnter={onMouseEnter ? () => onMouseEnter(artwork) : undefined}
            onMouseLeave={onMouseLeave}
            onMouseMove={onMouseMove}
            onCopyWallet={onCopyWallet}
            copiedWallet={copiedWallet}
            catalogType={catalogType}
            useAspectRatio={useAspectRatio}
          />
        </motion.div>
      ))}
    </Masonry>
  );

  if (!separateSeries) return renderGrid(artworks);
  return <div className="space-y-8">
    <div data-catalog-group="series">{renderGrid(series)}</div>
    <div data-catalog-group="artworks">{renderGrid(artworks.filter(item => item.type !== 'collection'))}</div>
  </div>;
}
